/**
 * SDL maturity levels (0–4) plus the "Not Applicable" option.
 *
 * These definitions are shared by every place a score is rendered — the
 * question buttons, the previous-answer callout and the scoring guide modal —
 * so the labels and their meaning cannot drift apart again. They mirror
 * `maturityLevels` in packages/backend/forms/sdl-questions.json; update both
 * together.
 */

export interface SdlMaturityLevel {
  score: number;
  label: string;
  /** What answering at this level means, shown in the scoring guide. */
  description: string;
  /** Badge colour classes used when the score is displayed as a chip. */
  badgeClass: string;
  /** Left-border/background classes used for the guide's definition cards. */
  cardClass: string;
}

export const SDL_MATURITY_LEVELS: readonly SdlMaturityLevel[] = [
  {
    score: 0,
    label: "Not performed",
    description: "No security activities are performed for this practice.",
    badgeClass: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200",
    cardClass: "border-l-red-500 bg-red-50 dark:bg-red-900/10",
  },
  {
    score: 1,
    label: "Initial",
    description:
      "Some security activities are performed, but they are informal (no documented process), inconsistent, or reactive.",
    badgeClass:
      "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200",
    cardClass: "border-l-orange-500 bg-orange-50 dark:bg-orange-900/10",
  },
  {
    score: 2,
    label: "Managed",
    description:
      "Security activities are performed regularly, with some process documentation and repeatability.",
    badgeClass:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200",
    cardClass: "border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/10",
  },
  {
    score: 3,
    label: "Practiced",
    description:
      "Security activities are well-defined, documented, and consistently executed. Metrics and improvements are tracked.",
    badgeClass:
      "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200",
    cardClass: "border-l-green-500 bg-green-50 dark:bg-green-900/10",
  },
  {
    score: 4,
    label: "Improving",
    description:
      "Security activities are fully integrated into development processes and continuously improved.",
    badgeClass:
      "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200",
    cardClass: "border-l-emerald-500 bg-emerald-50 dark:bg-emerald-900/10",
  },
];

/** Sentinel score stored for questions the product scopes out. */
export const SDL_NOT_APPLICABLE_SCORE = -1;

export const SDL_NOT_APPLICABLE_LABEL = "Not Applicable";

export const SDL_NOT_APPLICABLE_BADGE_CLASS =
  "bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200";

/**
 * Score -> label lookup, including the N/A sentinel, for compact call sites
 * that only need the name of a score.
 */
export const SDL_SCORE_LABELS: Record<number, string> = {
  [SDL_NOT_APPLICABLE_SCORE]: SDL_NOT_APPLICABLE_LABEL,
  ...Object.fromEntries(SDL_MATURITY_LEVELS.map((l) => [l.score, l.label])),
};
